/**
 * ============================================================================
 * useThresholdsQuery — Phase 6b frontend (Threshold Matrix Admin)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the threshold matrix — one row per metric
 *          with its current green/amber/red boundaries. The matrix is rarely
 *          edited (1-2 times per year per metric), so we don't poll; the
 *          screen refetches on mutation success.
 * Endpoint: GET /api/v1/quality-metrics/thresholds
 *
 * NOTE on `boundaries` shape: the server returns a Map keyed by the metric
 *  kind. For PERCENTAGE/COUNT we get `green_max`, `amber_max` (so a value
 *  ≤ green_max is GREEN, ≤ amber_max is AMBER, otherwise RED). For DAYS the
 *  same shape applies but values are in days. Inverse polarity metrics (e.g.,
 *  TRAINING_CURRENCY where higher is better) use `green_min`, `amber_min`.
 *  We model the union explicitly so the dialog form picks the right field
 *  set without a runtime guess.
 * ============================================================================ */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type { MetricKey, TrafficLightBand } from '../QualityMetricsDashboard/useDashboardQuery';

export type ThresholdPolarity = 'LOWER_IS_BETTER' | 'HIGHER_IS_BETTER';

export type ThresholdKind = 'COUNT' | 'PERCENTAGE' | 'DAYS';

/**
 * The three numeric boundaries that drive the traffic-light bands.
 *
 * For LOWER_IS_BETTER metrics (open deviations, doc overdue %, aging days):
 *   value ≤ green_max     → GREEN
 *   value ≤ amber_max     → AMBER
 *   value >  amber_max    → RED
 *
 * For HIGHER_IS_BETTER metrics (training currency %, right-first-time %):
 *   value ≥ green_min     → GREEN
 *   value ≥ amber_min     → AMBER
 *   value <  amber_min    → RED
 *
 * Some metrics (e.g., aging days) have an explicit RED ceiling; we accept an
 * optional `red_max` for completeness — the server uses it to clamp display.
 */
export interface ThresholdBoundaries {
  green_max?: number;
  amber_max?: number;
  green_min?: number;
  amber_min?: number;
  red_max?: number;
}

export interface MetricThreshold {
  metric_key: MetricKey;
  /** Localized metric label for display in the matrix. */
  label: string;
  kind: ThresholdKind;
  polarity: ThresholdPolarity;
  boundaries: ThresholdBoundaries;
  /** Active version of the threshold record (monotonic per metric). */
  version: number;
  /** Last update timestamp + signer bundle ids (display-only). */
  updated_at_utc: string | null;
  signature_bundle_ids: string[];
  /** What band the most recent value currently sits in — for the matrix dot. */
  current_band: TrafficLightBand;
}

export interface ThresholdsResponse {
  items: MetricThreshold[];
}

/**
 * Mock-api / contract-drift normalizer. The mock-api currently returns
 * `{ bands: { green:{max_days}, amber:{min_days,max_days}, red:{min_days} } }`
 * (the old draft-32 shape), but this screen was written against the
 * draft-42 `boundaries: { green_max, amber_max, ... }` shape. Until the
 * mock-api ships the new shape, we coerce here so the component never
 * crashes on `undefined.green_min`.
 */
function normalize(raw: unknown): ThresholdsResponse {
  const items = Array.isArray((raw as any)?.items) ? (raw as any).items : [];
  const out: MetricThreshold[] = items.map((it: any, i: number) => {
    if (it?.boundaries) return it as MetricThreshold;   // already correct shape
    const bands = it?.bands ?? {};
    const greenMax = bands.green?.max_days ?? bands.green?.max ?? bands.green?.max_pct;
    const amberMax = bands.amber?.max_days ?? bands.amber?.max ?? bands.amber?.max_pct;
    const greenMin = bands.green?.min_days ?? bands.green?.min ?? bands.green?.min_pct;
    const amberMin = bands.amber?.min_days ?? bands.amber?.min ?? bands.amber?.min_pct;
    // Crude polarity guess: if the response carries explicit `*_max` for green
    // it's LOWER_IS_BETTER; otherwise HIGHER_IS_BETTER.
    const polarity: ThresholdPolarity = (greenMax != null) ? 'LOWER_IS_BETTER' : 'HIGHER_IS_BETTER';
    return {
      metric_key: it.metric_key ?? `metric_${i}`,
      label: it.label ?? it.metric_key ?? `Metric ${i}`,
      kind: it.kind ?? (it.metric_key?.toLowerCase().includes('aging') ? 'DAYS' : 'PERCENTAGE'),
      polarity,
      boundaries: {
        green_max: greenMax,
        amber_max: amberMax,
        green_min: greenMin,
        amber_min: amberMin,
      },
      version: it.version ?? 1,
      updated_at_utc: it.updated_at_utc ?? null,
      signature_bundle_ids: Array.isArray(it.signature_bundle_ids) ? it.signature_bundle_ids : [],
      current_band: it.current_band ?? 'green',
    };
  });
  return { items: out };
}

export function useThresholdsQuery(): UseQueryResult<ThresholdsResponse> {
  return useQuery<ThresholdsResponse>({
    queryKey: ['quality-metrics', 'thresholds'],
    queryFn: ({ signal }) =>
      apiFetch<unknown>('/api/v1/quality-metrics/thresholds', { signal }).then(normalize),
  });
}
